/* ============================================================
   Sudsy Paws — landing page interactions (Shopify theme)
   (TypeScript source; compiled to /app.js, loaded as a module.)

   Runs as a Shopify theme section. All product data is injected by
   Liquid into `window.__SUDSY__` (variant ids, prices, asset CDN
   URLs, cart route). "Buy it now" adds the selected variant to the
   native Shopify cart (/cart/add.js) and sends the buyer to the
   hosted /checkout, after a confirmation step showing the total.
   ============================================================ */
const SUDSY = window.__SUDSY__ || {
    variants: [],
    assets: {},
    routes: { cartAdd: '/cart/add.js', checkout: '/checkout' },
    productName: 'Wash & Dry Station',
    apiBase: '',
};
/* product sizes (fallback labels/prices until live variant data loads) */
const SIZES = {
    '34': { label: '34"', price: '$479.00', priceAmount: 47900, variantId: null, available: true },
    '38': { label: '38"', price: '$699.00', priceAmount: 69900, variantId: null, available: true },
};
const SIZE_KEYS = ['34', '38'];
/* sizes we never sell from the storefront, regardless of Shopify stock */
const SOLD_OUT_SIZES = new Set(['34']);
/* asset() resolves a logical path to the Shopify CDN URL Liquid injected;
   falls back to the original path so the page still works outside a theme. */
function asset(path) {
    return (SUDSY.assets && SUDSY.assets[path]) || path;
}
const FALLBACK_GALLERY = [
    { src: asset('assets/main-1.jpg'), alt: 'Dog washing station main product view' },
    { src: asset('assets/main-2.jpg'), alt: 'Dog washing station detail view' },
    { src: asset('assets/main-3.jpg'), alt: 'Dog washing station dryer and tub view' },
    { src: asset('assets/main-4.jpg'), alt: 'Dog washing station faucet and controls view' },
    { src: asset('assets/main-5.jpg'), alt: 'Dog washing station accessories view' },
    { src: asset('assets/main-6.jpg'), alt: 'Dog washing station installation view' },
];
const GALLERY = Array.isArray(SUDSY.gallery) && SUDSY.gallery.length
    ? SUDSY.gallery
    : FALLBACK_GALLERY;
const state = {
    size: '38',
    qty: 1,
    cartCount: 0,
    mainImg: GALLERY[0].src,
};
let toastTimer = null;
let checkingOut = false;
/* ---- element refs ---- */
const mainImgEl = document.querySelector('[data-main-img]');
const galleryEl = document.querySelector('[data-gallery]');
const qtyEl = document.querySelector('[data-qty]');
const stickyBarEl = document.querySelector('[data-sticky-bar]');
const toastEl = document.querySelector('[data-toast]');
const cartBadgeEl = document.querySelector('[data-cart-badge]');
const cartCountEls = document.querySelectorAll('[data-cart-count]');
const priceEls = document.querySelectorAll('[data-price]');
const sizeLabelEls = document.querySelectorAll('[data-size-label]');
const sizeOptEls = document.querySelectorAll('[data-size-opt]');
const addBtns = document.querySelectorAll('[data-add-to-cart]');
/* ============================================================
   Size selection
   ============================================================ */
function refreshPills() {
    sizeOptEls.forEach((btn) => {
        const key = btn.getAttribute('data-size-opt');
        const priceEl = btn.querySelector('[data-size-price]');
        if (priceEl && SIZES[key])
            priceEl.textContent = SIZES[key].price;
    });
}
function selectSize(size) {
    if (!SIZES[size] || !SIZES[size].available)
        return; // ignore sold-out sizes
    state.size = size;
    const info = SIZES[size];
    priceEls.forEach((el) => (el.textContent = info.price));
    sizeLabelEls.forEach((el) => (el.textContent = info.label));
    sizeOptEls.forEach((btn) => {
        const active = btn.getAttribute('data-size-opt') === size;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });
}
function firstAvailableSize() {
    return SIZE_KEYS.find((k) => SIZES[k] && SIZES[k].available) || SIZE_KEYS[0];
}
/* reflect each size's availability: disable + label sold-out options */
function applyAvailability() {
    sizeOptEls.forEach((btn) => {
        const key = btn.getAttribute('data-size-opt');
        const info = SIZES[key];
        if (!info)
            return;
        const sold = !info.available;
        btn.classList.toggle('is-soldout', sold);
        btn.toggleAttribute('disabled', sold);
        btn.setAttribute('aria-disabled', sold ? 'true' : 'false');
        if (sold) {
            btn.classList.remove('is-active');
            btn.setAttribute('aria-checked', 'false');
        }
        const priceEl = btn.querySelector('[data-size-price]');
        if (priceEl)
            priceEl.textContent = sold ? 'Sold out' : info.price;
    });
}
function initSizeOptions() {
    sizeOptEls.forEach((btn) => {
        const key = btn.getAttribute('data-size-opt');
        btn.addEventListener('click', () => selectSize(key));
    });
    refreshPills();
    applyAvailability();
    if (!SIZES[state.size] || !SIZES[state.size].available) {
        state.size = firstAvailableSize();
    }
}
/* ============================================================
   Gallery
   ============================================================ */
function buildGallery() {
    if (!galleryEl)
        return;
    galleryEl.innerHTML = '';
    galleryEl.setAttribute('role', 'group');
    galleryEl.setAttribute('aria-label', 'Product image thumbnails');
    GALLERY.forEach((im) => {
        const active = im.src === state.mainImg;
        const btn = document.createElement('button');
        btn.className = 'thumb' + (active ? ' is-active' : '');
        btn.type = 'button';
        btn.setAttribute('aria-label', im.alt);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        const img = document.createElement('img');
        img.src = im.thumbSrc || im.src;
        img.alt = im.alt;
        btn.appendChild(img);
        btn.addEventListener('click', () => selectImage(im.src));
        galleryEl.appendChild(btn);
    });
}
function selectImage(src) {
    const selected = GALLERY.find((im) => im.src === src);
    if (!selected)
        return;
    state.mainImg = src;
    if (mainImgEl) {
        mainImgEl.src = src;
        mainImgEl.alt = selected.alt || '';
    }
    const thumbs = galleryEl ? galleryEl.querySelectorAll('.thumb') : [];
    GALLERY.forEach((im, i) => {
        if (!thumbs[i])
            return;
        const active = im.src === src;
        thumbs[i].classList.toggle('is-active', active);
        thumbs[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}
function initGallerySwipe() {
    if (!mainImgEl || GALLERY.length < 2)
        return;
    const MIN_DISTANCE = 48;
    const AXIS_RATIO = 1.25;
    let startX = null;
    let startY = null;
    const reset = () => {
        startX = null;
        startY = null;
    };
    const step = (delta) => {
        const found = GALLERY.findIndex((im) => im.src === state.mainImg);
        const current = found < 0 ? 0 : found;
        const next = (current + delta + GALLERY.length) % GALLERY.length;
        selectImage(GALLERY[next].src);
    };
    const stage = mainImgEl.closest('.hero__mainwrap');
    if (stage) {
        stage.setAttribute('role', 'region');
        stage.setAttribute('aria-roledescription', 'carousel');
        stage.setAttribute('aria-label', 'Product image gallery');
    }
    mainImgEl.tabIndex = 0;
    mainImgEl.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight');
    mainImgEl.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
            return;
        event.preventDefault();
        step(event.key === 'ArrowRight' ? 1 : -1);
    });
    mainImgEl.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) {
            reset();
            return;
        }
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
    }, { passive: true });
    mainImgEl.addEventListener('touchend', (event) => {
        if (startX === null || startY === null || event.changedTouches.length !== 1) {
            reset();
            return;
        }
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        const horizontalSwipe = Math.abs(dx) >= MIN_DISTANCE &&
            Math.abs(dx) > Math.abs(dy) * AXIS_RATIO;
        reset();
        if (!horizontalSwipe)
            return;
        if (event.cancelable)
            event.preventDefault();
        step(dx < 0 ? 1 : -1);
    }, { passive: false });
    mainImgEl.addEventListener('touchcancel', reset, { passive: true });
}
/* ============================================================
   Quantity
   ============================================================ */
function setQty(next) {
    state.qty = Math.max(1, Math.min(9, next));
    if (qtyEl)
        qtyEl.textContent = String(state.qty);
}
/* ============================================================
   Cart badge (kept in sync with the native Shopify cart count)
   ============================================================ */
function renderCart() {
    cartCountEls.forEach((el) => (el.textContent = String(state.cartCount)));
    if (cartBadgeEl)
        cartBadgeEl.hidden = state.cartCount <= 0;
}
function showToast() {
    if (!toastEl)
        return;
    toastEl.hidden = false;
    toastEl.style.animation = 'none';
    void toastEl.offsetWidth; // restart the fade-up animation
    toastEl.style.animation = '';
    if (toastTimer)
        clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
        toastEl.hidden = true;
    }, 2600);
}
/* ============================================================
   Money formatting (minor units -> display string)
   ============================================================ */
function money(cents) {
    return ('$' +
        (cents / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }));
}
// match a variant to size "34" / "38" by its option values (or title)
function sizeKeyForVariant(v) {
    const hay = (v.options || []).concat(v.title || '').join(' ');
    for (let i = 0; i < SIZE_KEYS.length; i++) {
        if (hay.indexOf(SIZE_KEYS[i]) !== -1)
            return SIZE_KEYS[i];
    }
    return null;
}
/* Read variant ids + prices straight from the Liquid-injected data. */
function loadProduct() {
    if (!SUDSY.variants || !SUDSY.variants.length) {
        console.info('[Sudsy Paws] No product selected for this section — using placeholder prices.');
        return;
    }
    SUDSY.variants.forEach((v) => {
        const key = sizeKeyForVariant(v);
        if (key && SIZES[key]) {
            SIZES[key].variantId = v.id;
            SIZES[key].price = v.priceText || money(v.price);
            SIZES[key].priceAmount = v.price;
            // sold out if Shopify is out of stock OR the size is in our no-sell list
            SIZES[key].available = v.available !== false && !SOLD_OUT_SIZES.has(key);
        }
    });
    refreshPills();
    applyAvailability();
    if (!SIZES[state.size] || !SIZES[state.size].available) {
        state.size = firstAvailableSize();
    }
    selectSize(state.size); // re-render with real prices for the available size
}
/* ============================================================
   Add to cart -> confirm total -> native Shopify checkout
   ============================================================ */
function addToCart() {
    const info = SIZES[state.size];
    if (!info || !info.variantId) {
        console.warn('[Sudsy Paws] No variant id for size ' + state.size + ' — is a product selected?');
        return;
    }
    showConfirm(info.priceAmount * state.qty);
}
async function confirmAndCheckout() {
    if (checkingOut)
        return;
    const info = SIZES[state.size];
    if (!info || !info.variantId)
        return;
    checkingOut = true;
    confirmOk?.setAttribute('disabled', 'true');
    try {
        const res = await fetch(SUDSY.routes.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: info.variantId, quantity: state.qty }),
        });
        if (!res.ok)
            throw new Error('cart/add failed: ' + res.status);
        window.location.href = SUDSY.routes.checkout; // -> hosted Shopify checkout
    }
    catch (err) {
        console.error('[Sudsy Paws] add-to-cart error', err);
        resetCheckout();
    }
}
function resetCheckout() {
    checkingOut = false;
    confirmOk?.removeAttribute('disabled');
    addBtns.forEach((b) => (b.disabled = false));
}
/* ---- order confirmation: show the total, then go to checkout ---- */
const confirmEl = document.querySelector('[data-confirm]');
const confirmItem = document.querySelector('[data-confirm-item]');
const confirmQty = document.querySelector('[data-confirm-qty]');
const confirmTotal = document.querySelector('[data-confirm-total]');
const confirmOk = document.querySelector('[data-confirm-ok]');
function showConfirm(totalCents) {
    if (confirmItem)
        confirmItem.textContent = SIZES[state.size].label + ' ' + SUDSY.productName;
    if (confirmQty)
        confirmQty.textContent = String(state.qty);
    if (confirmTotal)
        confirmTotal.textContent = money(totalCents);
    if (confirmEl)
        confirmEl.hidden = false;
    document.body.style.overflow = 'hidden';
}
function closeConfirm() {
    if (confirmEl)
        confirmEl.hidden = true;
    document.body.style.overflow = '';
}
function initConfirm() {
    if (!confirmEl)
        return;
    const cancel = document.querySelector('[data-confirm-cancel]');
    if (confirmOk)
        confirmOk.addEventListener('click', () => void confirmAndCheckout());
    if (cancel)
        cancel.addEventListener('click', closeConfirm);
    confirmEl.addEventListener('click', (e) => {
        if (e.target === confirmEl)
            closeConfirm();
    });
    document.addEventListener('keydown', (e) => {
        if (!confirmEl.hidden && e.key === 'Escape')
            closeConfirm();
    });
}
/* ============================================================
   Lightbox — click a product image to view it full size.
   ============================================================ */
function initLightbox() {
    const lb = document.querySelector('[data-lightbox]');
    const lbImg = document.querySelector('[data-lightbox-img]');
    const lbPrev = document.querySelector('[data-lightbox-prev]');
    const lbNext = document.querySelector('[data-lightbox-next]');
    const lbClose = document.querySelector('[data-lightbox-close]');
    if (!lb || !lbImg)
        return;
    let index = -1; // -1 = single image; >= 0 = position within GALLERY
    function open() {
        lb.hidden = false;
        document.body.style.overflow = 'hidden';
    }
    function close() {
        lb.hidden = true;
        document.body.style.overflow = '';
    }
    function showGallery(i) {
        index = (i + GALLERY.length) % GALLERY.length;
        const im = GALLERY[index];
        lbImg.src = im.src;
        lbImg.alt = im.alt;
        selectImage(im.src); // keep the hero thumbnail/main in sync
    }
    function openFromImage(img) {
        if (img.hasAttribute('data-main-img')) {
            // hero -> gallery mode
            let start = 0;
            GALLERY.forEach((im, i) => {
                if (im.src === img.getAttribute('src'))
                    start = i;
            });
            lbPrev.hidden = lbNext.hidden = GALLERY.length < 2;
            showGallery(start);
        }
        else {
            // standalone -> single mode
            index = -1;
            lbImg.src = img.getAttribute('src');
            lbImg.alt = img.getAttribute('alt') || '';
            lbPrev.hidden = lbNext.hidden = true;
        }
        open();
    }
    document.querySelectorAll('[data-zoom]').forEach((img) => {
        img.addEventListener('click', () => openFromImage(img));
    });
    lbClose.addEventListener('click', close);
    lbPrev.addEventListener('click', () => {
        if (index >= 0)
            showGallery(index - 1);
    });
    lbNext.addEventListener('click', () => {
        if (index >= 0)
            showGallery(index + 1);
    });
    lb.addEventListener('click', (e) => {
        if (e.target === lb)
            close();
    }); // backdrop
    document.addEventListener('keydown', (e) => {
        if (lb.hidden)
            return;
        if (e.key === 'Escape')
            close();
        else if (e.key === 'ArrowLeft' && index >= 0)
            showGallery(index - 1);
        else if (e.key === 'ArrowRight' && index >= 0)
            showGallery(index + 1);
    });
}
/* ============================================================
   Sticky bar
   ============================================================ */
function onScroll() {
    const show = window.scrollY > 760;
    if (stickyBarEl)
        stickyBarEl.hidden = !show;
}
/* ============================================================
   Scroll reveal animation
   ============================================================ */
function initReveal() {
    const revealEls = document.querySelectorAll('[data-reveal]');
    if (!revealEls.length)
        return;
    if (!('IntersectionObserver' in window)) {
        revealEls.forEach((el) => el.classList.add('is-visible'));
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting)
                return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
    }, { threshold: 0.14, rootMargin: '0px 0px -70px 0px' });
    revealEls.forEach((el) => observer.observe(el));
}
/* ============================================================
   Wire up
   ============================================================ */
addBtns.forEach((b) => b.addEventListener('click', addToCart));
const decBtn = document.querySelector('[data-qty-dec]');
const incBtn = document.querySelector('[data-qty-inc]');
if (decBtn)
    decBtn.addEventListener('click', () => setQty(state.qty - 1));
if (incBtn)
    incBtn.addEventListener('click', () => setQty(state.qty + 1));
window.addEventListener('scroll', onScroll, { passive: true });
loadProduct(); // read live variant ids + prices from the injected data first
initSizeOptions();
selectSize(state.size);
buildGallery();
initGallerySwipe();
initLightbox();
initConfirm();
initReveal();
renderCart();
onScroll();
export {};
